import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Predefined list of Indian stock symbols
const stockOptions = [
  { symbol: 'RELIANCE.BSE', name: 'Reliance Industries (BSE)' },
  { symbol: 'HDFCBANK.BSE', name: 'HDFC Bank (BSE)' },
  { symbol: 'TCS.BSE', name: 'Tata Consultancy Services (BSE)' },
  { symbol: 'INFY.BSE', name: 'Infosys (BSE)' },
  { symbol: 'SBIN.BSE', name: 'State Bank of India (BSE)' },
  { symbol: 'RELIANCE.NS', name: 'Reliance Industries (NSE)' },
  { symbol: 'HDFCBANK.NS', name: 'HDFC Bank (NSE)' },
  { symbol: 'TCS.NS', name: 'Tata Consultancy Services (NSE)' },
  { symbol: 'INFY.NS', name: 'Infosys (NSE)' },
  { symbol: 'SBIN.NS', name: 'State Bank of India (NSE)' },
];

const StockChart = () => {
  const [sensexData, setSensexData] = useState({});
  const [niftyData, setNiftyData] = useState({});
  const [stockData, setStockData] = useState({});
  const [stockSymbol, setStockSymbol] = useState('RELIANCE.BSE'); // Default stock
  const [inputSymbol, setInputSymbol] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  // Alpha Vantage API key
  const API_KEY = '38S2OQI8O13QHCNW';

  // Fetch data for a given symbol
  const fetchData = async (symbol, setData) => {
    try {
      const response = await axios.get(
        `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY&symbol=${symbol}&apikey=${API_KEY}`
      );
      if (response.data['Error Message']) {
        throw new Error(response.data['Error Message']);
      }
      const data = response.data['Time Series (Daily)'];
      if (data) {
        setData(data);
        setError(null);
      } else {
        throw new Error(`No data available for ${symbol}.`);
      }
    } catch (err) {
      setError(`Error fetching ${symbol}: ${err.message}`);
    }
  };

  // Search for valid symbols
  const searchSymbol = async (keyword) => {
    if (!keyword) return;
    try {
      const response = await axios.get(
        `https://www.alphavantage.co/query?function=SYMBOL_SEARCH&keywords=${keyword}&apikey=${API_KEY}`
      );
      if (response.data['bestMatches']) {
        setSearchResults(response.data['bestMatches']);
        setError(null);
      } else {
        setSearchResults([]);
        setError(`No symbols found for ${keyword}.`);
      }
    } catch (err) {
      setError(`Error searching symbols: ${err.message}`);
    }
  };

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchData('^BSESN', setSensexData), // Sensex
      fetchData('^NSEI', setNiftyData), // Nifty
      fetchData(stockSymbol, setStockData), // Selected stock
    ]);
    setLoading(false);
  }, [stockSymbol]);

  // Handle dropdown selection
  const handleSymbolSelect = (e) => {
    setStockSymbol(e.target.value);
    setInputSymbol(''); // Clear search input
    setSearchResults([]); // Clear search results
  };

  // Handle search input
  const handleSymbolChange = (e) => {
    setInputSymbol(e.target.value.toUpperCase());
  };

  const handleSymbolSubmit = (e) => {
    e.preventDefault();
    setStockSymbol(inputSymbol);
    searchSymbol(inputSymbol);
  };

  // Fetch data on mount and every 90 seconds
  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 90000); // Poll every 90 seconds
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Prepare chart data
  const createChartData = (data, label) => {
    const dates = data ? Object.keys(data).slice(0, 30).reverse() : [];
    const prices = dates.map((date) => parseFloat(data[date]['4. close'] || 0));
    return {
      labels: dates,
      datasets: [
        {
          label: `${label} Closing Price`,
          data: prices,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = (title) => ({
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: title },
    },
    scales: {
      x: { title: { display: true, text: 'Date' } },
      y: { title: { display: true, text: 'Price (INR)' } },
    },
  });

  return (
    <div style={{ width: '80%', margin: '0 auto', padding: '20px' }}>
      <h1>Indian Stock Market Dashboard</h1>
      <p>
        Sensex is up 4.58% in 2025, trading at ~81,721 as of May 23, 2025. Nifty recently closed at 24,637 on May 22, 2025.
      </p>

      {/* Dropdown for stock selection */}
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="stockSelect" style={{ marginRight: '10px' }}>
          Select Stock:
        </label>
        <select
          id="stockSelect"
          value={stockSymbol}
          onChange={handleSymbolSelect}
          style={{ padding: '10px', width: '300px' }}
        >
          {stockOptions.map((option) => (
            <option key={option.symbol} value={option.symbol}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      {/* Symbol search input */}
      <form onSubmit={handleSymbolSubmit} style={{ marginBottom: '20px' }}>
        <input
          type="text"
          value={inputSymbol}
          onChange={handleSymbolChange}
          placeholder="Or search for a stock (e.g., Reliance)"
          style={{ padding: '10px', marginRight: '10px', width: '300px' }}
        />
        <button type="submit" style={{ padding: '10px' }}>
          Search
        </button>
      </form>

      {/* Symbol search results */}
      {searchResults.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Search Results</h3>
          <ul>
            {searchResults.map((result) => (
              <li key={result['1. symbol']}>
                {result['1. symbol']} - {result['2. name']} ({result['4. region']}, {result['8. currency']})
                <button
                  onClick={() => {
                    setStockSymbol(result['1. symbol']);
                    setInputSymbol('');
                    setSearchResults([]);
                  }}
                  style={{ marginLeft: '10px' }}
                >
                  Select
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && <p>Loading data...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* Sensex Chart */}
      {!loading && !error && Object.keys(sensexData).length > 0 && (
        <div style={{ marginBottom: '40px' }}>
          <h2>Sensex (^BSESN)</h2>
          <Line
            data={createChartData(sensexData, 'Sensex')}
            options={chartOptions('Sensex Stock Index (Last 30 Days)')}
          />
        </div>
      )}

      {/* Nifty Chart */}
      {!loading && !error && Object.keys(niftyData).length > 0 && (
        <div style={{ marginBottom: '40px' }}>
          <h2>Nifty 50 (^NSEI)</h2>
          <Line
            data={createChartData(niftyData, 'Nifty 50')}
            options={chartOptions('Nifty 50 Stock Index (Last 30 Days)')}
          />
        </div>
      )}

      {/* Individual Stock Chart */}
      {!loading && !error && Object.keys(stockData).length > 0 && (
        <div>
          <h2>{stockSymbol}</h2>
          <Line
            data={createChartData(stockData, stockSymbol)}
            options={chartOptions(`${stockSymbol} Stock Price (Last 30 Days)`)}
          />
        </div>
      )}

      {!loading && !error && Object.keys(stockData).length === 0 && (
        <p>No data available for {stockSymbol}. Try another symbol from the dropdown.</p>
      )}
    </div>
  );
};

export default StockChart;